/* eslint-disable react/require-default-props */
import { useCallback, useState } from 'react';

import {
  ChangeRequestData,
  NoticeCompanyTeamData,
} from '../../../../../../api/theHive/notices/types';
import { Constants } from '../../../../../../constants/hiveNotices';
import {
  TeamCountersText,
  TeamDocumentsSectionBox,
  TeamDocumentsTitle,
  TeamHintText,
  TeamMembersCard,
} from '../styles';
import { getTeamCountersSummary } from '../utils';
import { TeamMemberAccordion } from './TeamMemberAccordion';

interface TeamDocumentsSectionProps {
  noticeCompanyId: number;
  team: NoticeCompanyTeamData | undefined;
  initiallyExpandedMemberId?: number;
  pendingChangeRequests: ChangeRequestData[];
  actionsDisabled: boolean;
  docLoading: boolean;
  handleReplace: (docId: number) => Promise<void>;
  handleApprove: (docId?: number) => Promise<void>;
  handleApproveWithReservation: (
    reservationNote: string,
    docId: number
  ) => Promise<void>;
  handleReject: (reason: string, docId?: number) => Promise<void>;
}

export function TeamDocumentsSection({
  noticeCompanyId,
  team,
  initiallyExpandedMemberId,
  pendingChangeRequests,
  actionsDisabled,
  docLoading,
  handleReplace,
  handleApprove,
  handleApproveWithReservation,
  handleReject,
}: TeamDocumentsSectionProps): JSX.Element | null {
  const [expandedIds, setExpandedIds] = useState<number[]>(() =>
    initiallyExpandedMemberId ? [initiallyExpandedMemberId] : []
  );

  const handleToggle = useCallback(
    (memberId: number, isExpanded: boolean): void =>
      setExpandedIds((previous) =>
        isExpanded
          ? [...previous, memberId]
          : previous.filter((id) => id !== memberId)
      ),
    []
  );

  if (!team || team.items.length === 0) return null;

  const counters = getTeamCountersSummary(team);

  return (
    <TeamDocumentsSectionBox>
      <TeamDocumentsTitle>{Constants.teamDocumentsTitle}</TeamDocumentsTitle>
      {counters && <TeamCountersText>{counters}</TeamCountersText>}
      <TeamHintText>{Constants.teamDocumentsHint}</TeamHintText>
      <TeamMembersCard>
        {team.items.map((user) => (
          <TeamMemberAccordion
            key={user.id}
            noticeCompanyId={noticeCompanyId}
            user={user}
            expanded={expandedIds.includes(user.id)}
            onToggle={handleToggle}
            pendingChangeRequests={pendingChangeRequests}
            actionsDisabled={actionsDisabled}
            docLoading={docLoading}
            handleReplace={handleReplace}
            handleApprove={handleApprove}
            handleApproveWithReservation={handleApproveWithReservation}
            handleReject={handleReject}
          />
        ))}
      </TeamMembersCard>
    </TeamDocumentsSectionBox>
  );
}
